import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { ProjectService } from '../services/projectService';
import './SearchableSelect.css';

/**
 * Async searchable dropdown for selecting a base project (rev = 0) for revisions.
 * Shows all client projects when dropdown opens (sorted by ID descending, newest first).
 * User can also search by typing (minimum 2 characters).
 * When showAllProjects=true, shows ALL base projects regardless of client.
 * Displays: "{job_number} - {name}"
 */
const BaseProjectSelect = ({
  clientId,
  value,
  onChange,
  placeholder = 'Search for project...',
  disabled = false,
  className = '',
  error = false,
  name = 'baseProjectId',
  showAllProjects = false  // When true, don't filter by client
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // State for initial projects loaded when dropdown opens
  const [initialProjects, setInitialProjects] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch selected project details if value is set but we don't have the project
  useEffect(() => {
    const fetchSelectedProject = async () => {
      if (value && !selectedProject) {
        try {
          const projects = await ProjectService.getBaseProjectsForClient(clientId);
          const project = projects.find(p => p.id === value);
          if (project) {
            setSelectedProject({
              id: project.id,
              label: `${project.job_number} - ${project.name}`,
              jobNumber: project.job_number,
              name: project.name,
              contactId: project.contact_id,
              clientId: project.client_id,
              projectTypeId: project.project_type_id
            });
          }
        } catch (err) {
          console.error('Error fetching selected project:', err);
        }
      }
    };
    fetchSelectedProject();
  }, [value, clientId, selectedProject]);

  // Load initial projects for client when dropdown opens
  // When showAllProjects=true, loads ALL base projects (no client filter)
  const loadInitialProjects = useCallback(async () => {
    if (!showAllProjects && !clientId) return;
    setLoadingInitial(true);
    try {
      // Pass null for clientId when showAllProjects is true to get all base projects
      const effectiveClientId = showAllProjects ? null : clientId;
      const projects = await ProjectService.getBaseProjectsForClient(effectiveClientId);
      const formatted = projects.map(project => ({
        id: project.id,
        label: `${project.job_number} - ${project.name}`,
        jobNumber: project.job_number,
        name: project.name,
        contactId: project.contact_id,
        clientId: project.client_id,
        projectTypeId: project.project_type_id
      }));
      setInitialProjects(formatted);
    } catch (err) {
      console.error('Error loading initial projects:', err);
      setInitialProjects([]);
    } finally {
      setLoadingInitial(false);
    }
  }, [clientId, showAllProjects]);

  // Clear initial projects when client changes (unless showing all)
  useEffect(() => {
    if (!showAllProjects) {
      setInitialProjects([]);
    }
  }, [clientId, showAllProjects]);

  // Search projects when query changes (debounced)
  // When showAllProjects=true, searches ALL base projects (no client filter)
  const searchProjects = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Pass null for clientId when showAllProjects is true
      const effectiveClientId = showAllProjects ? null : clientId;
      const projects = await ProjectService.searchBaseProjects(query, effectiveClientId);
      const formatted = projects.map(project => ({
        id: project.id,
        label: `${project.job_number} - ${project.name}`,
        jobNumber: project.job_number,
        name: project.name,
        contactId: project.contact_id,
        clientId: project.client_id,
        projectTypeId: project.project_type_id
      }));
      setSearchResults(formatted);
    } catch (err) {
      console.error('Error searching projects:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, showAllProjects]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      setLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchProjects(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
      setLoading(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchProjects]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation (uses displayProjects which is either searchResults or initialProjects)
  useEffect(() => {
    const currentProjects = searchQuery.length >= 2 ? searchResults : initialProjects;

    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < currentProjects.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : currentProjects.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && currentProjects[highlightedIndex]) {
            handleSelect(currentProjects[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, searchResults, initialProjects, searchQuery]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (project) => {
    setSelectedProject(project);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);

    onChange({
      target: { name, value: project.id },
      project
    });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedProject(null);
    setSearchQuery('');
    onChange({
      target: { name, value: '' },
      project: null
    });
  };

  const handleToggleOpen = () => {
    if (disabled) return;
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      setSearchQuery('');
      setSearchResults([]);
      setHighlightedIndex(-1);
      // Load initial projects when opening if we haven't loaded them yet
      // Load if: showAllProjects is true OR clientId is set
      if ((showAllProjects || clientId) && initialProjects.length === 0) {
        loadInitialProjects();
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Get the current display list (search results take priority over initial projects)
  const displayProjects = searchQuery.length >= 2 ? searchResults : initialProjects;

  return (
    <div
      className={`searchable-select ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
      ref={dropdownRef}
    >
      <div
        className="searchable-select-trigger"
        onClick={handleToggleOpen}
      >
        <div className="searchable-select-content">
          {selectedProject ? (
            <span className="searchable-select-value">
              {selectedProject.label}
            </span>
          ) : (
            <span className="searchable-select-placeholder">{placeholder}</span>
          )}
        </div>

        <div className="searchable-select-actions">
          {selectedProject && !disabled && (
            <button
              type="button"
              className="searchable-select-clear"
              onClick={handleClear}
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
          <div className="searchable-select-separator" />
          <ChevronDown
            size={16}
            className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <Search size={16} className="searchable-select-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="searchable-select-search-input"
              placeholder="Search or scroll to select..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setHighlightedIndex(-1);
              }}
              autoFocus
            />
          </div>

          <div className="searchable-select-options" ref={optionsRef}>
            {loading || loadingInitial ? (
              <div className="searchable-select-loading">
                {loading ? 'Searching...' : 'Loading projects...'}
              </div>
            ) : searchQuery.length >= 2 && searchResults.length === 0 ? (
              <div className="searchable-select-no-options">
                No projects found matching "{searchQuery}"
              </div>
            ) : displayProjects.length === 0 ? (
              <div className="searchable-select-no-options">
                {showAllProjects
                  ? 'No base projects found'
                  : clientId
                    ? 'No base projects found for this client'
                    : 'Select a client first'}
              </div>
            ) : (
              displayProjects.map((project, index) => (
                <div
                  key={project.id}
                  className={`searchable-select-option ${
                    project.id === value ? 'selected' : ''
                  } ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(project)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {project.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseProjectSelect;
